import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { orderAPI, productAPI, usersAPI } from '../../services/api';

// Interface for recent order
interface RecentOrder {
  id: string;
  orderno: string;
  totalamount: number;
  orderstatus: number;
  created: string;
  oc_cart_order_id?: string;
  // Add other fields as needed based on API response
}

// Interface for month-wise order status
interface MonthWiseOrder {
  pending: number;
  cancelled: number;
  in_progress: number;
  confirmed: number;
  rejected: number;
  delivered: number;
  total: number;
  // Add other fields as needed based on API response
}

// Interface for dashboard count data
interface DashboardCountData {
  total_products: number;
  total_orders: number;
  total_agents: number;
}

//Interface  for top agents by orders
interface TopAgentsbyOrders {
  id: string;
  name?: string | null;
  username?: string | null;
  email?: string | null;
  cellnumber?: string | null;
  countrycode?: string | null;
  orderCount?: number | null;
}

//Interface for top agents by orders
interface TopAgentsbyReedeemedPoints {
  id: string;
  name?: string | null;
  username?: string | null;
  email?: string | null;
  cellnumber?: string | null;
  countrycode?: string | null;
  redeemedPoints?: number | null;
}

// Interface for top selling product
interface TopSellingProduct {
  id: string;
  name: string;
  price: number;
  sellcounter: number;
  revenue?: number; // Calculated field
  productmedia?: Array<{
    id: string;
    productname: string;
    createdby: string;
    modifiedby: string | null;
    sequence: number;
    productId: string;
    orderId: string | null;
    deletedAt: string | null;
    created: string;
    modified: string;
    masterdetailId: string;
    invoiceId: string | null;
  }>;
  // Add other fields as needed based on API response
}

// Interface for near out of stock product
interface NearOutOfStockProduct {
id: string;
name: string;
availablequantity: number;
price: number;
category?: {
  name: string;
};
productmedia?: Array<{
  id: string;
  productname: string;
  createdby: string;
  modifiedby: string | null;
  sequence: number;
  productId: string;
  orderId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  masterdetailId: string;
  invoiceId: string | null;
}>;
// Add other fields as needed based on API response
}

// Interface for recent agent (expanded to match API response)
export interface RecentAgent {
  id: string;
  firstname?: string | null;
  lastname?: string | null;
  username?: string | null;
  email?: string | null;
  cellnumber?: string | null;
  countrycode?: string | null;
  agentcode?: string | null;
  roleId?: number;
  groupId?: string | null;
  userstatus?: number;
  billingaddress?: any[] | null;
  shippingaddress?: any[] | null;
  created?: string;
  modified?: string;
  group?: { id: string; name: string; status: number } | null;
  // keep any additional unknown fields
  [key: string]: any;
}

// Interface for dashboard state
interface DashboardState {
  recentOrders: RecentOrder[];
  topSellingProducts: TopSellingProduct[];
  nearOutOfStockProducts: NearOutOfStockProduct[];
  monthWiseOrders: MonthWiseOrder | null;
  dashboardCountData: DashboardCountData | null;
  recentAgents: RecentAgent[];                     // <-- added
  recentOrdersLoading: boolean;
  topSellingProductsLoading: boolean;
  nearOutOfStockProductsLoading: boolean;
  monthWiseOrdersLoading: boolean;
  dashboardCountDataLoading: boolean;
  recentAgentsLoading: boolean;                    // <-- added
  error: string | null;
  topAgentsbyOrders?: TopAgentsbyOrders[];         // <-- added
  topAgentsbyOrdersLoading?: boolean;              // <-- added
  topAgentsbyReedeemedPoints?: TopAgentsbyReedeemedPoints[]; // <-- added
  topAgentsbyReedeemedPointsLoading?: boolean;     // <-- added
}

// Initial state
const initialState: DashboardState = {
  recentOrders: [],
  topSellingProducts: [],
  nearOutOfStockProducts: [],
  monthWiseOrders: null,
  dashboardCountData: null,
  recentAgents: [],                                // <-- added
  recentOrdersLoading: false,
  topSellingProductsLoading: false,
  nearOutOfStockProductsLoading: false,
  monthWiseOrdersLoading: false,
  dashboardCountDataLoading: false,
  recentAgentsLoading: false,                      // <-- added
  error: null,
  topAgentsbyOrders: [],                            // <-- added
  topAgentsbyOrdersLoading: false,                 // <-- added
  topAgentsbyReedeemedPoints: [],                  // <-- added
  topAgentsbyReedeemedPointsLoading: false,        // <-- added
};

// Async thunk for fetching recent orders
export const fetchRecentOrders = createAsyncThunk(
  'dashboard/fetchRecentOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await orderAPI.getRecentOrders();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch recent orders'
      );
    }
  }
);

// Async thunk for fetching top agents by orders
export const fetchTopAgentsByOrders = createAsyncThunk(
  'dashboard/fetchTopAgentsByOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await orderAPI.getTopAgentsbyOrders();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch top agents by orders'
      );
    }
  }
);

// Async thunk for fetching top agents by reedeemed points
export const fetchTopAgentsByReedeemedPoints = createAsyncThunk(
  'dashboard/fetchTopAgentsByReedeemedPoints',
  async (_, { rejectWithValue }) => {
    try {
      const response = await orderAPI.getTopAgentsbyReedeemedPoints();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch top agents by reedeemed points'
      );
    }
  }
);

// Async thunk for fetching recent agents (recent users - roleId=2)
export const fetchRecentAgents = createAsyncThunk(
  'dashboard/fetchRecentAgents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await usersAPI.getRecentUsers();
      // API returns a wrapper: { responseStatus, responseMessage, data: [...] }
      // return the inner array when present, otherwise fall back to response.data
      const payload = response?.data;
      if (payload?.data && Array.isArray(payload.data)) return payload.data;
      // If API already returned an array directly
      if (Array.isArray(payload)) return payload;
      return [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch recent agents'
      );
    }
  }
);

// Async thunk for fetching top selling products
export const fetchTopSellingProducts = createAsyncThunk(
  'dashboard/fetchTopSellingProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productAPI.getTopSellingProducts();
      // Calculate revenue for each product
      const productsWithRevenue = (response.data || []).map((product: any) => ({
        ...product,
        revenue: (product.price || 0) * (product.sellcounter || 0)
      }));
      return productsWithRevenue;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch top selling products'
      );
    }
  }
);

// Async thunk for fetching near out of stock products
export const fetchNearOutOfStockProducts = createAsyncThunk(
  'dashboard/fetchNearOutOfStockProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productAPI.getNearOutOfStockProducts();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch near out of stock products'
      );
    }
  }
);

// Async thunk for fetching month-wise orders
export const fetchMonthWiseOrders = createAsyncThunk(
  'dashboard/fetchMonthWiseOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await orderAPI.getMonthWiseOrders();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch month-wise orders'
      );
    }
  }
);

// Async thunk for fetching dashboard count data
export const fetchDashboardCountData = createAsyncThunk(
  'dashboard/fetchDashboardCountData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await orderAPI.getDashboardCountData();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch dashboard count data'
      );
    }
  }
);

// Dashboard slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearRecentOrders: (state) => {
      state.recentOrders = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch recent orders
      .addCase(fetchRecentOrders.pending, (state) => {
        state.recentOrdersLoading = true;
        state.error = null;
      })
      .addCase(fetchRecentOrders.fulfilled, (state, action) => {
        state.recentOrdersLoading = false;
        state.recentOrders = action.payload || [];
        state.error = null;
      })
      .addCase(fetchRecentOrders.rejected, (state, action) => {
        state.recentOrdersLoading = false;
        state.error = action.payload as string;
      })
      // Fetch recent agents
      .addCase(fetchRecentAgents.pending, (state) => {
        state.recentAgentsLoading = true;
        state.error = null;
      })
      .addCase(fetchRecentAgents.fulfilled, (state, action) => {
        state.recentAgentsLoading = false;
        state.recentAgents = action.payload || [];
        state.error = null;
      })
      .addCase(fetchRecentAgents.rejected, (state, action) => {
        state.recentAgentsLoading = false;
        state.error = action.payload as string;
      })
      // Fetch top selling products
      .addCase(fetchTopSellingProducts.pending, (state) => {
        state.topSellingProductsLoading = true;
        state.error = null;
      })
      .addCase(fetchTopSellingProducts.fulfilled, (state, action) => {
        state.topSellingProductsLoading = false;
        state.topSellingProducts = action.payload || [];
        state.error = null;
      })
      .addCase(fetchTopSellingProducts.rejected, (state, action) => {
        state.topSellingProductsLoading = false;
        state.error = action.payload as string;
      })
      // Fetch near out of stock products
      .addCase(fetchNearOutOfStockProducts.pending, (state) => {
        state.nearOutOfStockProductsLoading = true;
        state.error = null;
      })
      .addCase(fetchNearOutOfStockProducts.fulfilled, (state, action) => {
        state.nearOutOfStockProductsLoading = false;
        state.nearOutOfStockProducts = action.payload || [];
        state.error = null;
      })
      .addCase(fetchNearOutOfStockProducts.rejected, (state, action) => {
        state.nearOutOfStockProductsLoading = false;
        state.error = action.payload as string;
      })
      // Fetch month-wise orders
      .addCase(fetchMonthWiseOrders.pending, (state) => {
        state.monthWiseOrdersLoading = true;
        state.error = null;
      })
      .addCase(fetchMonthWiseOrders.fulfilled, (state, action) => {
        state.monthWiseOrdersLoading = false;
        state.monthWiseOrders = action.payload || null;
        state.error = null;
      })
      .addCase(fetchMonthWiseOrders.rejected, (state, action) => {
        state.monthWiseOrdersLoading = false;
        state.error = action.payload as string;
      })
      // Fetch dashboard count data
      .addCase(fetchDashboardCountData.pending, (state) => {
        state.dashboardCountDataLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardCountData.fulfilled, (state, action) => {
        state.dashboardCountDataLoading = false;
        state.dashboardCountData = action.payload || null;
        state.error = null;
      })
      .addCase(fetchDashboardCountData.rejected, (state, action) => {
        state.dashboardCountDataLoading = false;
        state.error = action.payload as string;
      })
      // Fetch top agents by orders
      .addCase(fetchTopAgentsByOrders.pending, (state) => {
        state.dashboardCountDataLoading = true;
        state.error = null;
      })
      .addCase(fetchTopAgentsByOrders.fulfilled, (state, action) => {
        state.dashboardCountDataLoading = false;
        // Assuming you want to store top agents somewhere, add a new field if needed
        state.topAgentsbyOrders = action.payload || [];
        state.error = null;
      })
      .addCase(fetchTopAgentsByOrders.rejected, (state, action) => {
        state.dashboardCountDataLoading = false;
        state.error = action.payload as string;
      })
      // Fetch top agents by reedeemed points
      .addCase(fetchTopAgentsByReedeemedPoints.pending, (state) => {
        state.topAgentsbyReedeemedPointsLoading = true;
        state.error = null;
      })
      .addCase(fetchTopAgentsByReedeemedPoints.fulfilled, (state, action) => {
        state.topAgentsbyReedeemedPointsLoading = false;
        // Assuming you want to store top agents somewhere, add a new field if needed
        state.topAgentsbyReedeemedPoints = action.payload || [];
        state.error = null;
      })
      .addCase(fetchTopAgentsByReedeemedPoints.rejected, (state, action) => {
        state.topAgentsbyReedeemedPointsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearRecentOrders } = dashboardSlice.actions;
export default dashboardSlice.reducer;